import {
  Button,
  Column,
  Columns,
  FlexBox,
  Icon,
  Text,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { useKey } from 'react-use';

import { ConversationPreview } from 'components/conversation/conversationPreview';
import { useCommandLauncher } from 'utils/commandLauncher';

// Shortcuts apply anywhere in the launcher, except while typing in another field.
const isShortcutTarget = ({ target }: KeyboardEvent): boolean =>
  !(target instanceof HTMLElement) ||
  target.hasAttribute('data-command-launcher-input') ||
  !(
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable
  );

type ConversationScopeListPreviewProps = {
  /** The id of the conversation to preview. */
  readonly csid: number;
  /** The 1-based position of the conversation in the results. */
  readonly resultPosition: number;
  /** The formatted total number of results. */
  readonly resultsCount: string;
  /** Called when going back to the search results. */
  readonly onBack: VoidFunction;
  /** Previews the previous result, or disables the button when omitted. */
  readonly onPrevious?: VoidFunction;
  /** Previews the next result, or disables the button when omitted. */
  readonly onNext?: VoidFunction;
  /** Called when navigating to the conversation. */
  readonly onNavigateToConversation: (
    e?: MouseEvent<HTMLAnchorElement>,
  ) => void;
};

/** Previews a conversation inline in the command launcher. */
export const ConversationScopeListPreview = ({
  csid,
  onBack,
  onNavigateToConversation,
  resultPosition,
  resultsCount,
  onPrevious: onPreviousProp,
  onNext: onNextProp,
}: ConversationScopeListPreviewProps): JSX.Element => {
  const { setHints, setIsInputReadOnly } = useCommandLauncher();
  const [isLoading, setIsLoading] = useState(true);

  const onPrevious = isLoading ? undefined : onPreviousProp;
  const onNext = isLoading ? undefined : onNextProp;

  const handleStep = useCallback(
    (direction: 'previous' | 'next') => {
      const onStep = direction === 'previous' ? onPrevious : onNext;
      if (!onStep) return;

      ampli.commandPaletteConversationPreviewNavigated({
        direction,
      });

      onStep();
    },
    [onPrevious, onNext],
  );

  useEffect(() => {
    setIsInputReadOnly(true);

    setHints([
      { keys: '⌫', label: 'Back' },
      { keys: '←', label: 'Previous' },
      { keys: '→', label: 'Next' },
      { keys: 'esc', label: 'Close' },
    ]);

    return () => {
      setIsInputReadOnly(false);
      setHints(undefined);
    };
  }, [setHints, setIsInputReadOnly]);

  // Backspace goes back to the list.
  useKey(
    (event) => event.key === 'Backspace' && isShortcutTarget(event),
    (event) => {
      event.preventDefault();
      onBack();
    },
    { event: 'keydown' },
    [onBack],
  );

  // Arrows step through the results.
  useKey(
    (event) =>
      (event.key === 'ArrowLeft' || event.key === 'ArrowRight') &&
      isShortcutTarget(event),
    (event) => {
      event.preventDefault();
      handleStep(event.key === 'ArrowLeft' ? 'previous' : 'next');
    },
    { event: 'keydown' },
    [handleStep],
  );

  return (
    <Columns>
      <Column>
        <Column.Row
          noMinHeight
          noHorizontalPadding
          noVerticalPadding
          borderBottom
        >
          <FlexBox
            alignItems="center"
            justifyContent="space-between"
            gap="200"
            padding="250"
          >
            <Button variant="tertiary" icon="arrow-left" onClick={onBack}>
              Back to results
            </Button>

            <FlexBox alignItems="center" gap="200">
              <Text.Paragraph variant="3" color="secondary" wrap="nowrap">
                {resultPosition.toLocaleString(navigator.language)} of{' '}
                {resultsCount}
              </Text.Paragraph>

              <Button
                variant="tertiary"
                slotStart={<Icon name="angle-left" />}
                disabled={!onPrevious}
                onClick={() => handleStep('previous')}
              >
                Previous
              </Button>

              <Button
                variant="tertiary"
                slotEnd={<Icon name="angle-right" />}
                disabled={!onNext}
                onClick={() => handleStep('next')}
              >
                Next
              </Button>
            </FlexBox>
          </FlexBox>
        </Column.Row>

        <Column.Row
          scrollable
          noMinHeight
          noHorizontalPadding
          noVerticalPadding
        >
          <ConversationPreview
            csid={csid}
            onNavigateToConversation={onNavigateToConversation}
            onLoadingChange={setIsLoading}
          />
        </Column.Row>
      </Column>
    </Columns>
  );
};
